import React, { useState, useContext, useEffect, useRef } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	FormControlLabel,
	Checkbox,
	Box,
	Typography,
	ListSubheader,
	Alert,
	CircularProgress,
	Chip,
	FormHelperText,
	Divider,
} from '@mui/material';
import AuthContext from '../../store/auth-context';
import SnackbarContext from '../../store/snackbar-context';
import OlympicsContext from '../../store/olympics-context';
import { createCampaign, previewCampaign, updateCampaign } from '../../api/email-campaign-api';
import ConfirmationDialog from './ConfirmationDialog';

const STAGE3_COHORT_OPTIONS = [
	{ value: 'all', label: 'Wszyscy' },
	{ value: 'iiia_no_oral', label: 'Finaliści IIIA' },
	{ value: 'iiib_no_oral', label: 'IIIB bez udziału w ustnym' },
	{ value: 'iiib_finalist', label: 'Finaliści IIIB' },
	{ value: 'laureate', label: 'Laureaci IIIB' },
	{ value: 'no_certificate_number', label: 'Z tytułem bez numeru' },
	{ value: 'manual_r1', label: 'R1 — poprawki (ręczna)' },
	{
		value: 'no_zip_excluded_from_cohorts',
		label: 'Bez ZIP',
	},
] as const;

interface NewEmailCampaignDialogProps {
	open: boolean;
	onClose: () => void;
	onSuccess: () => void;
	mode?: 'create' | 'edit' | 'duplicate';
	campaignId?: number;
	initialData?: {
		name?: string;
		subject?: string;
		message?: string;
		target_preset?: string;
		target_districts?: number[];
		target_custom_sql?: string;
		stage3_scan_cohort?: string;
		require_link_verification?: boolean;
		require_password_verification?: boolean;
		require_code_verification?: boolean;
		enable_student_merge?: boolean;
		student_filter?: string;
		is_test?: boolean;
		is_active?: boolean;
		visible_to?: string;
	};
	initialPdfAttachmentName?: string | null;
	focusDistricts?: boolean;
}

const NewEmailCampaignDialog: React.FC<NewEmailCampaignDialogProps> = ({
	open,
	onClose,
	onSuccess,
	mode = 'create',
	campaignId,
	initialData,
	initialPdfAttachmentName,
	focusDistricts = false,
}) => {
	const { token, role } = useContext(AuthContext);
	const { setMsg } = useContext(SnackbarContext);
	const { districts } = useContext(OlympicsContext);

	const emptyFormData = {
		name: '',
		subject: '',
		message: '',
		target_preset: 'all_teachers',
		target_districts: [] as number[],
		target_custom_sql: '',
		stage3_scan_cohort: '',
		require_link_verification: false,
		require_password_verification: false,
		require_code_verification: false,
		enable_student_merge: false,
		student_filter: 'none',
		is_test: false,
		is_active: true,
		visible_to: 'admin_only',
	};

	const [formData, setFormData] = useState(emptyFormData);
	const initialFormDataRef = useRef(emptyFormData);

	const [pdfFile, setPdfFile] = useState<File | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [previewOpen, setPreviewOpen] = useState(false);
	const [previewLoading, setPreviewLoading] = useState(false);
	const [previewError, setPreviewError] = useState('');
	const [previewRecipientId, setPreviewRecipientId] = useState<number | ''>('');
	const [previewData, setPreviewData] = useState<{
		recipient: {
			recipient_type: string;
			recipient_email: string;
			recipient_name: string;
		};
		subject: string;
		html_body: string;
		plain_body: string;
		candidates: Array<{
			recipient_id: number;
			recipient_type: string;
			recipient_email: string;
			recipient_name: string;
		}>;
		total_count: number;
		selected_recipient_id: number | null;
		preview_pdf_base64?: string | null;
	} | null>(null);

	const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
	const [confirmCloseText, setConfirmCloseText] = useState<string | null>(null);

	const isAdmin = role === 'administrator';
	const isEditMode = mode === 'edit';
	const hasExistingPdf = isEditMode && !!initialPdfAttachmentName;
	const districtsRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		if (!open) {
			return;
		}
		const nextFormData = {
			...emptyFormData,
			...(initialData || {}),
			target_districts: initialData?.target_districts ?? [],
			target_custom_sql: initialData?.target_custom_sql ?? '',
			stage3_scan_cohort: initialData?.stage3_scan_cohort ?? '',
			student_filter: initialData?.student_filter ?? 'none',
		};
		setFormData(nextFormData);
		initialFormDataRef.current = nextFormData;
		setPdfFile(null);
		setErrors({});
		setPreviewData(null);
		setPreviewError('');
		setPreviewRecipientId('');
		setPreviewOpen(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, initialData, mode]);

	useEffect(() => {
		let objectUrl: string | null = null;
		if (previewData?.preview_pdf_base64) {
			const bin = atob(previewData.preview_pdf_base64);
			const bytes = new Uint8Array(bin.length);
			for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
			const blob = new Blob([bytes], { type: 'application/pdf' });
			objectUrl = window.URL.createObjectURL(blob);
		}
		setPreviewPdfUrl(objectUrl);
		return () => {
			if (objectUrl) {
				window.URL.revokeObjectURL(objectUrl);
			}
		};
	}, [previewData?.preview_pdf_base64]);

	useEffect(() => {
		if (!open || !focusDistricts) {
			return;
		}
		if (!formData.target_preset.startsWith('district_')) {
			return;
		}
		const timer = window.setTimeout(() => {
			districtsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
		}, 0);
		return () => window.clearTimeout(timer);
	}, [open, focusDistricts, formData.target_preset]);

	const handleInputChange = (field: string, value: any) => {
		setFormData((prev) => {
			if (field === 'enable_student_merge' && value === true) {
				return {
					...prev,
					[field]: value,
					target_preset: 'district_students',
				};
			}
			return {
				...prev,
				[field]: value,
			};
		});

		// Clear error when user starts typing
		if (errors[field]) {
			setErrors((prev) => ({
				...prev,
				[field]: '',
			}));
		}
	};

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (file) {
			setPdfFile(file);
		}
	};

	const validateForm = () => {
		const newErrors: Record<string, string> = {};

		if (!formData.name.trim()) {
			newErrors.name = 'Nazwa wysyłki jest wymagana';
		}

		if (!formData.subject.trim()) {
			newErrors.subject = 'Temat wiadomości jest wymagany';
		}

		if (!formData.message.trim()) {
			newErrors.message = 'Treść wiadomości jest wymagana';
		}

		// District validation for district_* presets
		if (formData.target_preset.startsWith('district_') && formData.target_districts.length === 0) {
			newErrors.target_districts = 'Wybierz co najmniej jeden okręg';
		}

		// Custom SQL validation
		if (formData.target_preset === 'custom_sql' && !formData.target_custom_sql.trim()) {
			newErrors.target_custom_sql = 'SQL WHERE clause jest wymagany';
		}

		if (formData.target_preset === 'stage3_signed_scan_cohort' && !formData.stage3_scan_cohort?.trim()) {
			newErrors.stage3_scan_cohort = 'Wybierz kohortę zaświadczeń III';
		}

		// Code verification requires PDF
		if (formData.require_code_verification && !pdfFile && !hasExistingPdf) {
			newErrors.pdf_file = 'Załącznik PDF jest wymagany dla weryfikacji kodem';
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const isFormDirty = () => {
		const current = JSON.stringify(formData);
		const initial = JSON.stringify(initialFormDataRef.current);
		return current !== initial || !!pdfFile;
	};

	const performClose = () => {
		setConfirmCloseText(null);
		setPreviewOpen(false);
		setPreviewData(null);
		onClose();
	};

	const handleCloseRequest = () => {
		if (isFormDirty()) {
			setConfirmCloseText('Masz niezapisane zmiany. Na pewno chcesz zamknąć?');
			return;
		}
		performClose();
	};

	const handleSubmit = async () => {
		if (!token || !validateForm()) return;

		try {
			setIsLoading(true);

			const response = isEditMode
				? await updateCampaign(token, campaignId || 0, buildFormData())
				: await createCampaign(token, buildFormData());

			if (response.status === 201 || response.status === 200) {
				setMsg({
					msg: isEditMode ? 'Wysyłka została zaktualizowana' : 'Wysyłka została utworzona pomyślnie',
					severity: 'success',
				});
				onSuccess();
				performClose();
			} else {
				setMsg({
					msg:
						response.data?.message || (isEditMode ? 'Błąd podczas zapisu wysyłki' : 'Błąd podczas tworzenia wysyłki'),
					severity: 'error',
				});
			}
		} catch (exception: any) {
			console.error(exception);
			const errorMsg =
				exception?.response?.data?.message ||
				exception?.message ||
				(isEditMode ? 'Błąd podczas zapisu wysyłki' : 'Błąd podczas tworzenia wysyłki');
			setMsg({ msg: errorMsg, severity: 'error' });
		} finally {
			setIsLoading(false);
		}
	};

	const buildFormData = (selectedRecipientId?: number | '') => {
		const formDataToSend = new FormData();

		Object.entries(formData).forEach(([key, value]) => {
			if (key === 'target_districts') {
				formDataToSend.append(key, JSON.stringify(value));
			} else {
				formDataToSend.append(key, value.toString());
			}
		});

		if (pdfFile) {
			formDataToSend.append('pdf_template_file', pdfFile);
		}

		if (selectedRecipientId) {
			formDataToSend.append('preview_recipient_id', selectedRecipientId.toString());
		}

		return formDataToSend;
	};

	const handlePreview = async () => {
		if (!token || !validateForm()) return;
		setPreviewError('');
		setPreviewLoading(true);
		setPreviewData(null);
		try {
			const response = await previewCampaign(token, buildFormData());
			const preview = response.data.preview;
			setPreviewData(preview);
			setPreviewRecipientId(preview.selected_recipient_id || '');
			setPreviewOpen(true);
		} catch (exception: any) {
			const errorMsg = exception?.response?.data?.message || exception?.message || 'Błąd podglądu';
			setPreviewError(errorMsg);
			setPreviewData(null);
			setPreviewOpen(true);
		} finally {
			setPreviewLoading(false);
		}
	};

	const handlePreviewRecipientChange = async (value: number | '') => {
		if (!token) return;
		setPreviewRecipientId(value);
		setPreviewLoading(true);
		setPreviewError('');
		setPreviewData(null);
		try {
			const response = await previewCampaign(token, buildFormData(value));
			const preview = response.data.preview;
			setPreviewData(preview);
			setPreviewRecipientId(preview.selected_recipient_id || '');
		} catch (exception: any) {
			const errorMsg = exception?.response?.data?.message || exception?.message || 'Błąd podglądu';
			setPreviewError(errorMsg);
			setPreviewData(null);
		} finally {
			setPreviewLoading(false);
		}
	};

	const getTargetPresetLabel = (preset: string) => {
		switch (preset) {
			case 'all_teachers':
				return 'Wszyscy nauczyciele';
			case 'all_secretaries':
				return 'Wszyscy sekretarze';
			case 'all_secretaries_and_teachers':
				return 'Wszyscy sekretarze i nauczyciele';
			case 'all_students':
				return 'Wszyscy uczniowie';
			case 'laureate_students':
				return 'Laureaci (IIIB)';
			case 'laureates_teachers':
				return 'Nauczyciele laureatów';
			case 'district_teachers':
				return 'Nauczyciele z wybranych okręgów';
			case 'district_secretaries':
				return 'Sekretarze z wybranych okręgów';
			case 'district_students':
				return 'Uczniowie z wybranych okręgów';
			case 'custom_sql':
				return 'Zaawansowane (SQL WHERE)';
			case 'stage3_signed_scan_cohort':
				return 'Zaświadczenia III — skany (kohorta)';
			default:
				return preset;
		}
	};

	const getVisibilityLabel = (visibility: string) => {
		switch (visibility) {
			case 'admin_only':
				return 'Tylko administrator';
			case 'admin_secretary':
				return 'Administrator + Sekretarze';
			case 'admin_secretary_teacher':
				return 'Administrator + Sekretarze + Nauczyciele';
			case 'all':
				return 'Wszyscy';
			default:
				return visibility;
		}
	};

	const showDistrictSelection = formData.target_preset.startsWith('district_');
	const showCustomSql = formData.target_preset === 'custom_sql';
	const showStage3Cohort = formData.target_preset === 'stage3_signed_scan_cohort';
	const showStudentFilter =
		formData.enable_student_merge &&
		(formData.target_preset === 'all_students' || formData.target_preset === 'district_students');

	return (
		<>
			<Dialog
				open={open}
				onClose={handleCloseRequest}
				maxWidth='md'
				fullWidth
			>
				<DialogTitle>
					{isEditMode
						? 'Edytuj wysyłkę mailingową'
						: mode === 'duplicate'
							? 'Utwórz wysyłkę na podstawie'
							: 'Nowa wysyłka mailingowa'}
				</DialogTitle>

				<DialogContent>
					<Box
						display='flex'
						flexDirection='column'
						gap={3}
						pt={1}
					>
						{/* Basic Information */}
						<TextField
							label='Nazwa wysyłki'
							value={formData.name}
							onChange={(e) => handleInputChange('name', e.target.value)}
							fullWidth
							error={!!errors.name}
							helperText={errors.name}
							required
						/>

						<TextField
							label='Temat wiadomości'
							value={formData.subject}
							onChange={(e) => handleInputChange('subject', e.target.value)}
							fullWidth
							error={!!errors.subject}
							helperText={errors.subject}
							required
						/>

						<TextField
							label='Treść wiadomości'
							value={formData.message}
							onChange={(e) => handleInputChange('message', e.target.value)}
							fullWidth
							multiline
							rows={4}
							error={!!errors.message}
							helperText={errors.message}
							required
						/>

						<FormControl>
							<FormControlLabel
								control={
									<Checkbox
										checked={formData.enable_student_merge}
										onChange={(e) => handleInputChange('enable_student_merge', e.target.checked)}
									/>
								}
								label='Włącz pola danych ucznia i wyników (np. {student.first_name}, {result.second_stage_total_points})'
							/>
							<FormHelperText>
								Działa tylko dla wysyłek do uczniów; dla innych adresatów tagi pozostają puste.
							</FormHelperText>
						</FormControl>

						{formData.enable_student_merge && (
							<Box>
								<Typography
									variant='subtitle2'
									gutterBottom
								>
									Dostępne tagi (działają w temacie i treści)
								</Typography>
								<Typography variant='body2'>
									Możesz używać dowolnych kolumn z tabel `students` oraz `results` w formacie
									{` {student.kolumna} / {result.kolumna} (snake_case).`}
								</Typography>
								<Typography variant='body2'>
									{'Przykłady: {student.first_name} {student.last_name} {student.email} {student.parent_email}'}
								</Typography>
								<Typography variant='body2'>
									{
										'{result.first_stage_points} {result.second_b_oral_sum} {result.second_b_oral_description} {result.second_b_one_stage_points} {result.second_b_two_stage_points} {result.second_b_three_stage_points}'
									}
								</Typography>
							</Box>
						)}

						{/* Target Group */}
						<FormControl fullWidth>
							<InputLabel>Grupa docelowa</InputLabel>
							<Select
								value={formData.target_preset}
								label='Grupa docelowa'
								onChange={(e) => handleInputChange('target_preset', e.target.value)}
							>
								<ListSubheader sx={{ lineHeight: 1.2, fontWeight: 600 }}>Uczniowie</ListSubheader>
								<MenuItem value='all_students'>Wszyscy uczniowie</MenuItem>
								<MenuItem value='laureate_students'>Laureaci (tytuł Laureat w IIIB)</MenuItem>
								<MenuItem value='district_students'>Uczniowie z okręgów…</MenuItem>
								<ListSubheader sx={{ lineHeight: 1.2, fontWeight: 600 }}>Nauczyciele</ListSubheader>
								<MenuItem value='all_teachers'>Wszyscy nauczyciele</MenuItem>
								<MenuItem value='laureates_teachers'>Nauczyciele laureatów (powiązani z Laureatem w IIIB)</MenuItem>
								<MenuItem value='district_teachers'>Nauczyciele z okręgów…</MenuItem>
								<ListSubheader sx={{ lineHeight: 1.2, fontWeight: 600 }}>Sekretarze i inne</ListSubheader>
								<MenuItem value='all_secretaries'>Wszyscy sekretarze</MenuItem>
								<MenuItem value='all_secretaries_and_teachers'>Wszyscy sekretarze i nauczyciele</MenuItem>
								<MenuItem value='district_secretaries'>Sekretarze z okręgów…</MenuItem>
								{isAdmin && <MenuItem value='custom_sql'>Zaawansowane (SQL WHERE)</MenuItem>}
								{isAdmin && (
									<MenuItem value='stage3_signed_scan_cohort'>Zaświadczenia III — skany podpisane (kohorta)</MenuItem>
								)}
							</Select>
						</FormControl>

						{showStage3Cohort && (
							<FormControl
								fullWidth
								error={!!errors.stage3_scan_cohort}
							>
								<InputLabel id='stage3-cohort-label'>Kohorta zaświadczeń III</InputLabel>
								<Select
									labelId='stage3-cohort-label'
									label='Kohorta zaświadczeń III'
									value={formData.stage3_scan_cohort || ''}
									onChange={(e) => handleInputChange('stage3_scan_cohort', e.target.value)}
								>
									{STAGE3_COHORT_OPTIONS.map((o) => (
										<MenuItem
											key={o.value}
											value={o.value}
										>
											{o.label}
										</MenuItem>
									))}
								</Select>
								{errors.stage3_scan_cohort && <FormHelperText>{errors.stage3_scan_cohort}</FormHelperText>}
								<FormHelperText>
									Ta sama kolejność co przy zbiorczym ZIP; załącznik PDF per uczeń z zapisanych skanów.
								</FormHelperText>
							</FormControl>
						)}

						{/* District Selection */}
						{showDistrictSelection && (
							<FormControl
								ref={districtsRef}
								fullWidth
								error={!!errors.target_districts}
							>
								<InputLabel>Wybierz okręgi</InputLabel>
								<Select
									multiple
									value={formData.target_districts}
									label='Wybierz okręgi'
									onChange={(e) => handleInputChange('target_districts', e.target.value)}
									renderValue={(selected) => (
										<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
											{(selected as number[]).map((value) => {
												const district = districts.find((d) => d.id === value);
												return (
													<Chip
														key={value}
														label={district?.name || value}
														size='small'
													/>
												);
											})}
										</Box>
									)}
								>
									{districts.map((district) => (
										<MenuItem
											key={district.id}
											value={district.id}
										>
											{district.name}
										</MenuItem>
									))}
								</Select>
								{errors.target_districts && <FormHelperText>{errors.target_districts}</FormHelperText>}
							</FormControl>
						)}

						{/* Custom SQL */}
						{showCustomSql && (
							<TextField
								label='SQL WHERE clause'
								value={formData.target_custom_sql}
								onChange={(e) => handleInputChange('target_custom_sql', e.target.value)}
								fullWidth
								multiline
								rows={3}
								error={!!errors.target_custom_sql}
								helperText={
									errors.target_custom_sql ||
									'Tylko dla administratorów. Przykład: approved = 1 AND district_id IN (1,2,3)'
								}
								required
							/>
						)}

						{showStudentFilter && (
							<FormControl fullWidth>
								<InputLabel>Filtry uczniów</InputLabel>
								<Select
									value={formData.student_filter}
									label='Filtry uczniów'
									onChange={(e) => handleInputChange('student_filter', e.target.value)}
								>
									<MenuItem value='none'>Brak</MenuItem>
									<MenuItem value='approved'>Potwierdzony</MenuItem>
									<MenuItem value='first_stage_points_approved'>Uzyskał punkty w etapie I (potwierdzony)</MenuItem>
									<MenuItem value='first_stage_points_unapproved'>Uzyskał punkty w etapie I (niepotwierdzony)</MenuItem>
									<MenuItem value='first_stage_qualified'>Zakwalifikowany do etapu IIA</MenuItem>
									<MenuItem value='second_a_points'>Uzyskał punkty w etapie IIA</MenuItem>
									<MenuItem value='second_a_qualified'>Zakwalifikowany do etapu IIB</MenuItem>
									<MenuItem value='second_b_points'>Uzyskał punkty w etapie IIB</MenuItem>
									<MenuItem value='second_b_oral_description'>Wpisana charakterystyka w etapie IIB</MenuItem>
									<MenuItem value='second_b_qualified'>Zakwalifikowany do etapu IIIA</MenuItem>
									<MenuItem value='third_a_points'>Uzyskał punkty w etapie IIIA</MenuItem>
									<MenuItem value='third_a_qualified'>Zakwalifikowany do etapu IIIB</MenuItem>
									<MenuItem value='third_b_points'>Uzyskał punkty w etapie IIIB</MenuItem>
								</Select>
							</FormControl>
						)}

						{/* PDF Upload */}
						<Box>
							<Typography
								variant='subtitle2'
								gutterBottom
							>
								Załącznik PDF (opcjonalny)
							</Typography>
							<input
								type='file'
								accept='.pdf,.html'
								onChange={handleFileChange}
								style={{ marginBottom: 8 }}
							/>
							{pdfFile && (
								<Typography
									variant='body2'
									color='text.secondary'
								>
									Wybrany plik: {pdfFile.name}
								</Typography>
							)}
							{!pdfFile && hasExistingPdf && (
								<Typography
									variant='body2'
									color='text.secondary'
								>
									Aktualny plik: {initialPdfAttachmentName}
								</Typography>
							)}
							{errors.pdf_file && (
								<Typography
									variant='body2'
									color='error'
								>
									{errors.pdf_file}
								</Typography>
							)}
						</Box>

						{/* Verification Options */}
						<Box>
							<Typography
								variant='subtitle2'
								gutterBottom
							>
								Opcje weryfikacji
							</Typography>
							<FormControlLabel
								control={
									<Checkbox
										checked={formData.require_link_verification}
										onChange={(e) => handleInputChange('require_link_verification', e.target.checked)}
									/>
								}
								label='Wymagaj potwierdzenia kliknięcia w link'
							/>
							{formData.require_link_verification && (
								<Typography
									variant='body2'
									color='text.secondary'
								>
									Dostępne placeholdery dla umiejscowienia linku: {'{link}'} lub {'{link:tekst}'}
								</Typography>
							)}
							<FormControlLabel
								control={
									<Checkbox
										checked={formData.require_password_verification}
										onChange={(e) => handleInputChange('require_password_verification', e.target.checked)}
									/>
								}
								label='Wymagaj weryfikacji hasłem'
							/>
							<FormControlLabel
								control={
									<Checkbox
										checked={formData.require_code_verification}
										onChange={(e) => handleInputChange('require_code_verification', e.target.checked)}
										disabled={!pdfFile && !hasExistingPdf}
									/>
								}
								label='Wymagaj kodu weryfikacyjnego z PDF'
							/>
							{!pdfFile && formData.require_code_verification && !hasExistingPdf && (
								<Typography
									variant='body2'
									color='text.secondary'
								>
									Załącznik PDF jest wymagany dla weryfikacji kodem
								</Typography>
							)}
						</Box>

						{/* Campaign Settings */}
						<Box>
							<Typography
								variant='subtitle2'
								gutterBottom
							>
								Ustawienia wysyłki
							</Typography>
							<FormControlLabel
								control={
									<Checkbox
										checked={formData.is_test}
										onChange={(e) => handleInputChange('is_test', e.target.checked)}
									/>
								}
								label='Wysyłka testowa'
							/>
							<FormControlLabel
								control={
									<Checkbox
										checked={formData.is_active}
										onChange={(e) => handleInputChange('is_active', e.target.checked)}
									/>
								}
								label='Aktywna'
							/>
						</Box>

						{/* Visibility */}
						<FormControl fullWidth>
							<InputLabel>Widoczna dla</InputLabel>
							<Select
								value={formData.visible_to}
								label='Widoczna dla'
								onChange={(e) => handleInputChange('visible_to', e.target.value)}
							>
								<MenuItem value='admin_only'>Tylko administrator</MenuItem>
								<MenuItem value='admin_secretary'>Administrator + Sekretarze</MenuItem>
								<MenuItem value='admin_secretary_teacher'>Administrator + Sekretarze + Nauczyciele</MenuItem>
								<MenuItem value='all'>Wszyscy</MenuItem>
							</Select>
						</FormControl>

						{previewError && <Alert severity='error'>{previewError}</Alert>}
						{/* Info */}
						<Alert severity='info'>
							Możesz wyłączyć wszystkie opcje weryfikacji dla mailingu czysto informacyjnego. Kod weryfikacyjny będzie
							automatycznie wstawiony w miejsce "XXXX" w załączonym PDF.
						</Alert>
					</Box>
				</DialogContent>

				<DialogActions>
					<Button
						onClick={handleCloseRequest}
						disabled={isLoading}
					>
						Anuluj
					</Button>
					<Button
						onClick={handlePreview}
						disabled={isLoading || previewLoading}
					>
						{previewLoading ? 'Podgląd...' : 'Podgląd'}
					</Button>
					<Button
						onClick={handleSubmit}
						variant='contained'
						disabled={isLoading}
						startIcon={isLoading ? <CircularProgress size={20} /> : undefined}
					>
						{isLoading
							? isEditMode
								? 'Zapisywanie...'
								: 'Tworzenie...'
							: isEditMode
								? 'Zapisz zmiany'
								: 'Utwórz wysyłkę'}
					</Button>
				</DialogActions>
			</Dialog>
			<Dialog
				open={previewOpen}
				onClose={() => setPreviewOpen(false)}
				maxWidth='md'
				fullWidth
			>
				<DialogTitle>Podgląd wiadomości</DialogTitle>
				<DialogContent>
					{previewData ? (
						<Box
							display='flex'
							flexDirection='column'
							gap={2}
							pt={1}
						>
							<Typography variant='body2'>
								<strong>Liczba odbiorców spełniających warunek:</strong> {previewData.total_count}
							</Typography>
							{previewData.candidates.length > 0 && (
								<FormControl fullWidth>
									<InputLabel>Wybierz odbiorcę do podglądu</InputLabel>
									<Select
										value={previewRecipientId}
										label='Wybierz odbiorcę do podglądu'
										onChange={(e) => handlePreviewRecipientChange(e.target.value === '' ? '' : Number(e.target.value))}
									>
										{previewData.candidates.map((candidate) => (
											<MenuItem
												key={`${candidate.recipient_type}-${candidate.recipient_id}`}
												value={candidate.recipient_id}
											>
												{candidate.recipient_name} ({candidate.recipient_email})
											</MenuItem>
										))}
									</Select>
								</FormControl>
							)}
							<Typography variant='body2'>
								<strong>Odbiorca:</strong> {previewData.recipient.recipient_name} (
								{previewData.recipient.recipient_email})
							</Typography>
							<Typography variant='body2'>
								<strong>Typ:</strong> {previewData.recipient.recipient_type}
							</Typography>
							<Typography variant='body2'>
								<strong>Temat:</strong> {previewData.subject}
							</Typography>
							<Divider />
							<Typography variant='subtitle2'>Treść (HTML)</Typography>
							<Box
								sx={{
									border: '1px solid #ddd',
									borderRadius: 1,
									p: 2,
									backgroundColor: '#fafafa',
								}}
								dangerouslySetInnerHTML={{ __html: previewData.html_body }}
							/>
							{previewPdfUrl && (
								<>
									<Divider sx={{ my: 1 }} />
									<Typography variant='subtitle2'>Załącznik PDF (jak w wysyłce do wybranego odbiorcy)</Typography>
									<Typography
										variant='caption'
										color='text.secondary'
										display='block'
										sx={{ mb: 1 }}
									>
										Sprawdź, czy plik odpowiada temu uczniowi — treść wiadomości i PDF muszą być spójne.
									</Typography>
									<Box
										component='iframe'
										title='Podgląd załącznika PDF'
										sx={{
											width: '100%',
											height: 560,
											border: '1px solid #ccc',
											borderRadius: 1,
										}}
										src={`${previewPdfUrl}#toolbar=0`}
									/>
								</>
							)}
						</Box>
					) : (
						<Typography variant='body2'>Brak danych podglądu.</Typography>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setPreviewOpen(false)}>Zamknij</Button>
				</DialogActions>
			</Dialog>
			<ConfirmationDialog
				close={() => setConfirmCloseText(null)}
				inProgress={false}
				text={confirmCloseText}
				onSuccess={performClose}
			/>
		</>
	);
};

export default NewEmailCampaignDialog;
